import { createRouter, createWebHistory } from 'vue-router';
import { authService } from '@/services/auth.service';
import Community from '@/views/PageCommunity.vue';
import About from '@/views/PageAbout.vue';
import NotFound from '@/views/PageNotFound.vue';
import Profile from '@/views/PageProfile.vue';
import { paths } from '@/utils/paths';
import PropositionsPage from '@/components/PropositionsPage.vue';
import ConsultationsPage from '@/views/PageConsultation.vue';
import { useCommunityStore } from '@/store/communityStore';
import { communitiesService } from '@/services/communities.service';
import CustomPage from '@/views/PageCustom.vue';
import { customPageService } from '@/services/customPage.service';
import { constant } from '@/constant';
import ProjectPage from '@/views/PageProject.vue';
import { useUserStore } from '@/store/userStore';
import { projectsService } from '@/services/projects.service';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 };
  },
  routes: [
    {
      path: paths.root,
      name: 'home',
      component: Community,
    },
    {
      path: paths.consultations_list,
      name: 'consultations-list',
      component: Community,
    },
    {
      path: paths.login,
      name: 'login',
      component: { template: '<div></div>' },
      beforeEnter: async (to, from, next) => {
        let query = '';
        if (to.query.consultation) {
          query = `consultations/${to.query.consultation as string}`;
          if (to.query.section) {
            query += `?section=${to.query.section as string}`;
            if (to.query.form) {
              query += `&form=${to.query.form as string}`;
            }
          }
        } else if (to.query.projectSlug) {
          const communityStore = useCommunityStore();
          const userStore = useUserStore();
          const dtoProject = await projectsService.getProjectBySlug(to.query.projectSlug as string);
          const project = communityStore.getProject(dtoProject.id);
          const result = communityStore.isUserAllowedAccessProject(project, userStore.user);
          if (result.allowed) {
            query = `projects/${project.slug}`;
          } else {
            query = `?projectId=${project.id}`;
          }
        }
        next(`/${query}`);
      },
    },
    {
      path: paths.logout,
      name: 'logout',
      component: { template: '<div></div>' },
      beforeEnter: async (to, from, next) => {
        await authService.logoutUser();
        next('/');
      },
    },
    {
      path: paths.consultations,
      name: 'consultations',
      component: ConsultationsPage,
      props: {
        isTemplate: false,
      },
    },
    {
      path: paths.consultationTemplates,
      name: 'consultationTemplates',
      component: ConsultationsPage,
      props: {
        isTemplate: true,
      },
    },
    {
      path: paths.project,
      name: 'project',
      component: ProjectPage,
      props: {
        isTemplate: false,
      },
    },
    {
      path: paths.projectTemplate,
      name: 'projectTemplate',
      component: ProjectPage,
      props: {
        isTemplate: true,
      },
    },
    {
      path: paths.propositions,
      name: 'propositions',
      component: PropositionsPage,
    },
    {
      path: paths.about,
      name: 'about-us',
      component: About,
    },
    {
      path: paths.profile,
      name: 'profile',
      component: Profile,
    },
    {
      path: '/:locale(en|fr)',
      name: 'locale',
      component: Community,
    },
    {
      path: '/:slug(.*)', // wildcard route for custom pages
      name: 'custom-page',
      component: CustomPage,
      beforeEnter: async (to, from, next) => {
        const communityStore = useCommunityStore();
        const slug = Array.isArray(to.params.slug) ? to.params.slug[0] : to.params.slug;
        const page = await communityStore.getCustomPageBySlug(slug);
        if (!page) {
          console.debug('page not found');
          next({ name: 'not-found' });
        } else if (page.type === constant.Community.CustomPage.Redirect) {
          const locale = communityStore.community?.defaultLanguage ?? constant.DefaultLocale;
          next(page.content?.[locale]);
        }
        next();
      },
    },
    {
      path: '/not-found',
      name: 'not-found',
      component: NotFound,
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (to.name === 'login' && to.query.token) {
    const token = to.query.token as string;
    await authService.loginUser(token);
  } else if (!useUserStore().user?.id) {
    await authService.initUser();
  }
  const communityStore = useCommunityStore();
  if (!communityStore.isLoaded) {
    const community = await communitiesService.getCommunity();
    if (community) {
      communityStore.setCommunity(community);
      await customPageService.getCustomPages();
    }
  }
  next();
});
export default router;
